import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Truck, ScanSearch, Loader2, Search, PackageCheck, FileText, Filter,
  CheckCircle2, AlertTriangle, Plus,
} from "lucide-react";
import OffSitePaperworkDialog from "../components/OffSitePaperworkDialog";
import NewExpectedDeliveryDialog from "../components/NewExpectedDeliveryDialog";
import ReceiveOffSiteDialog from "../components/ReceiveOffSiteDialog";
import { formatDate } from "../lib/format";
import { subscribe } from "../lib/sync";

const statusChip = (s) =>
  s === "complete" ? "bg-emerald-100 text-emerald-800" :
  s === "partial" ? "bg-blue-100 text-blue-800" :
  s === "pending" ? "bg-slate-100 text-slate-700" :
  "bg-slate-100 text-slate-700";

export default function OffSiteDeliveriesPage() {
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState([]);
  const [tab, setTab] = useState("open");
  const [search, setSearch] = useState("");
  const [paperworkOpen, setPaperworkOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expected-deliveries");
      setDeliveries(data);
    } catch {
      toast.error("Could not load deliveries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Live sync — refetch when any other window mutates a delivery
  useEffect(() => {
    return subscribe((msg) => {
      if (msg?.type === "delivery-updated") load();
    });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deliveries.filter((d) => {
      if (tab === "open" && d.status === "complete") return false;
      if (tab === "complete" && d.status !== "complete") return false;
      if (!q) return true;
      const blob = [
        d.reference, d.project_name, d.vendor, d.delivery_location, d.note,
        ...(d.lines || []).map((l) => `${l.sku} ${l.item_name}`),
      ].join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [deliveries, tab, search]);

  const openReceive = (d) => {
    setActive(d);
    setReceiveOpen(true);
  };

  const openCount = deliveries.filter((d) => d.status !== "complete").length;
  const completeCount = deliveries.filter((d) => d.status === "complete").length;
  const unreviewedExtras = deliveries.reduce(
    (n, d) => n + (d.lines || []).filter((l) => l.unexpected && !l.reviewed).length,
    0
  );

  return (
    <div>
      <PageHeader
        eyebrow="Field Ops"
        title="Off-Site Deliveries"
        description="Deliveries that go directly to a job site. Scan the paperwork before it arrives, then check items off as they land."
        testId="offsite-header"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setManualOpen(true)}
              variant="outline"
              data-testid="new-offsite-manual-btn"
              className="rounded-sm border-blue-300 text-blue-800 hover:bg-blue-50"
            >
              <Plus size={16} className="mr-1.5" /> New Delivery
            </Button>
            <Button
              onClick={() => setPaperworkOpen(true)}
              data-testid="new-offsite-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              <ScanSearch size={16} className="mr-1.5" /> Scan Paperwork
            </Button>
          </div>
        }
      />

      <div className="p-4 md:p-8 space-y-6">
        {/* Summary tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Tile icon={Truck} label="Open Deliveries" value={openCount} tone="blue" />
          <Tile icon={CheckCircle2} label="Complete" value={completeCount} tone="emerald" />
          <Tile
            icon={AlertTriangle}
            label="Unreviewed Extras"
            value={unreviewedExtras}
            tone={unreviewedExtras > 0 ? "red" : "slate"}
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-3 border-b border-slate-200 flex items-center gap-3">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="rounded-sm bg-slate-100">
                <TabsTrigger value="open" data-testid="tab-open" className="rounded-sm data-[state=active]:bg-white">
                  Open
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold font-mono rounded-sm bg-blue-800 text-white">
                    {openCount}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="complete" data-testid="tab-complete" className="rounded-sm data-[state=active]:bg-white">
                  Complete
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold font-mono rounded-sm bg-slate-500 text-white">
                    {completeCount}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="all" data-testid="tab-all" className="rounded-sm data-[state=active]:bg-white">
                  All
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="ml-auto relative w-full max-w-xs">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search PO, part, vendor, project…"
                data-testid="offsite-search"
                className="rounded-sm pl-8 h-9"
              />
            </div>
          </div>

          <div className="p-0" data-testid="offsite-list-panel">
            {loading ? (
              <div className="p-10 text-center text-sm text-slate-500">
                <Loader2 size={24} className="mx-auto animate-spin text-slate-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Truck size={40} className="mx-auto text-slate-400" />
                <div className="mt-3 font-display text-xl font-bold text-slate-900">
                  Nothing matches
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  Try a different tab or scan a piece of paperwork to create your first off-site delivery.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {filtered.map((d) => {
                  const unrev = (d.lines || []).filter((l) => l.unexpected && !l.reviewed).length;
                  return (
                    <li key={d.id} className="p-4 flex flex-wrap gap-3 items-center" data-testid={`offsite-row-${d.id}`}>
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${statusChip(d.status)}`}>
                            {d.status}
                          </span>
                          <span className="font-mono text-sm font-semibold text-slate-900">
                            {d.reference || "no reference"}
                          </span>
                          {d.vendor && (
                            <span className="text-xs text-slate-500">· {d.vendor}</span>
                          )}
                          {(d.original_paperwork || []).length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700">
                              <FileText size={10} /> paperwork
                            </span>
                          )}
                          {unrev > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-800 animate-pulse">
                              <AlertTriangle size={10} /> {unrev} to review
                            </span>
                          )}
                        </div>
                        <div className="mt-1 font-semibold text-slate-900">{d.project_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {d.expected_at && <>Expected {formatDate(d.expected_at.slice(0, 10))} · </>}
                          {d.totals?.received || 0}/{d.totals?.expected || 0} received
                          {d.totals?.remaining ? ` · ${d.totals.remaining} remaining` : ""}
                          {d.delivery_location && <> · {d.delivery_location}</>}
                        </div>
                      </div>
                      <Button
                        onClick={() => openReceive(d)}
                        data-testid={`open-receive-${d.id}`}
                        className="rounded-sm bg-blue-800 hover:bg-blue-900 text-white"
                      >
                        <PackageCheck size={14} className="mr-1.5" /> Open · Receive
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <OffSitePaperworkDialog
        open={paperworkOpen}
        onOpenChange={setPaperworkOpen}
        onSuccess={load}
      />
      <NewExpectedDeliveryDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onSuccess={load}
      />
      <ReceiveOffSiteDialog
        open={receiveOpen}
        onOpenChange={setReceiveOpen}
        delivery={active}
        onChanged={(d) => {
          setActive(d);
          // Merge back into list
          setDeliveries((all) => all.map((x) => (x.id === d.id ? d : x)));
        }}
      />
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone = "slate" }) {
  const toneCls = {
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[tone];
  return (
    <div className={`border rounded-md p-4 flex items-center gap-3 ${toneCls}`}>
      <div className="grid place-items-center h-10 w-10 rounded-sm bg-white/70 border border-current/10">
        <Icon size={20} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
        <div className="font-display text-2xl font-black tracking-tight">{value}</div>
      </div>
    </div>
  );
}
